import * as THREE from 'three'
import Scene from '../index.js'
import fragment from '../../../shaders/fragment.glsl'
import vertex from '../../../shaders/vertex.glsl'

class Particles
{
    constructor(obj)
    {
        this.scene = Scene.instance
        this.fragment = fragment
        this.vertex = vertex
        this.group = new THREE.Group()
        this.raycaster = Scene.raycaster
        this.img = obj.img
        this.numPixels = this.img.width * this.img.height
        this.ratio = this.img.width / this.img.height
        this.canvasVisible = false

        this.createPixelData()
        this.setParticlesGeometry()
        this.setParticlesMaterial()
        this.setParticlesMesh()
        this.setTouchArea()
        this.addGroup()

        this.raycaster.on('intersect', () => 
        {
            this.particlesMaterial.uniforms.u_touch.value = this.trail.texture
        })
    }

    createPixelData()
    {
        const canvas = document.createElement('canvas')
        canvas.width = this.img.width
        canvas.height = this.img.height

        const ctx = canvas.getContext('2d')
        ctx.scale(-1, -1); 
        ctx.drawImage(this.img, 0, 0, canvas.width * -1, canvas.height * -1);

        this.imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data   

        if(this.canvasVisible)
        {
            document.body.appendChild(canvas)
            canvas.style.position = 'fixed'
            canvas.style.backgroundColor = "white"
            canvas.style.right = 0
            canvas.style.zIndex = 100
        }
    }

    setParticlesGeometry()
    {
        this.particlesGeometry = new THREE.BufferGeometry()

        const pPos = new Float32Array(this.numPixels * 3)
        const pIndex = new Float32Array(this.numPixels)
        const pAngle = new Float32Array(this.numPixels)
        const pColor = new Float32Array(this.numPixels * 4)
        const pUv = new Float32Array(this.numPixels * 2)
     
        let originalWidth = this.img.width
        let originalHeight = this.img.height
        let ratio = originalWidth / originalHeight

        let targetWidth = 1
        let targetHeight = targetWidth / ratio

        let minX = - targetWidth * 0.5
        let maxX = targetWidth * 0.5
        let minY = - targetHeight * 0.5
        let maxY = targetHeight * 0.5

        for (let i = 0; i < this.numPixels; i++)
        {
            let posX = i % originalWidth
            let posY = Math.floor(i / originalWidth)
            
            pPos[i * 3 + 0] = THREE.MathUtils.mapLinear(posX, 0, originalWidth, minX, maxX)
            pPos[i * 3 + 1] =  THREE.MathUtils.mapLinear(posY, 0, originalHeight, minY, maxY)
            pPos[i * 3 + 2] = 0

            pUv[i * 2 + 0] = 1 - THREE.MathUtils.mapLinear(posX, 0, originalWidth, 0, 1)
            pUv[i * 2 + 1] = THREE.MathUtils.mapLinear(posY, 0, originalHeight, 0, 1)

            pIndex[i] = i;
            pAngle[i] = Math.random() * Math.PI

            pColor[i * 4 + 0] = this.imageData[i * 4 + 0] / 255
            pColor[i * 4 + 1] = this.imageData[i * 4 + 1] / 255
            pColor[i * 4 + 2] = this.imageData[i * 4 + 2] / 255
            pColor[i * 4 + 3] = this.imageData[i * 4 + 3]
        }

        this.particlesGeometry.setAttribute('position', new THREE.BufferAttribute(pPos, 3))
        this.particlesGeometry.setAttribute('a_angle', new THREE.BufferAttribute(pAngle))
        this.particlesGeometry.setAttribute('a_index', new THREE.BufferAttribute(pIndex))
        this.particlesGeometry.setAttribute('color', new THREE.BufferAttribute(pColor, 4))
        this.particlesGeometry.setAttribute('uv', new THREE.BufferAttribute(pUv, 2))
    }

    setParticlesMaterial()
    {
        this.particlesMaterial = new THREE.ShaderMaterial({
            vertexShader: this.vertex, 
            fragmentShader : this.fragment, 
            depthTest: false,
            transparent: true,
            uniforms: {
                u_touch: { value: null } 
            }
        })
    }

    setParticlesMesh()
    {
        this.particlesMesh = new THREE.Points(this.particlesGeometry, this.particlesMaterial)
        this.particlesMesh.rotation.y = Math.PI
        this.group.add(this.particlesMesh)
    }

    setTouchArea()
    {
        this.particlesGeometry.computeBoundingBox()
        let height = Math.abs(this.particlesGeometry.boundingBox.min.y - this.particlesGeometry.boundingBox.max.y)
        let width = Math.abs(this.particlesGeometry.boundingBox.min.x - this.particlesGeometry.boundingBox.max.x)

        this.touchGeometry = new THREE.PlaneGeometry(width, height, 100, 100)
        this.touchMaterial = new THREE.MeshBasicMaterial()
        this.touchMesh = new THREE.Mesh(this.touchGeometry, this.touchMaterial)
        this.touchMesh.visible = false
        this.group.add(this.touchMesh)
        this.raycaster.setObject(this.touchMesh)
    }

    addGroup(){
        this.scene.add(this.group)
    }

    destroy() {
        if (this.particlesMesh) {
            if (this.particlesMesh.geometry) this.particlesMesh.geometry.dispose()
            if (this.particlesMaterial) this.particlesMaterial.dispose()
        }

        if (this.touchGeometry) this.touchGeometry.dispose()
        if (this.touchMaterial) this.touchMaterial.dispose()
    }
}

export default Particles